<script setup lang="ts">
import { useImageBase64 } from '@/composables/useImageBase64';
import { useUserStore } from '@/stores/useUserStore';
import type { ShareData } from '@/types/share';
import html2canvas from 'html2canvas-pro';
import { computed, nextTick, ref, watch } from 'vue';

const { user } = useUserStore();

const restaurant = computed(() => user?.restaurants[0]);

const logoUrl = computed(() => restaurant.value?.logo_url ?? null);
const { imageSrc: logoImageSrc, isLoading: isLogoLoading } = useImageBase64(logoUrl);

const props = defineProps<{
  shareData: ShareData;
  format: string; // e.g., 'storyMainDish', 'postFull'
  nutritionalData?: {
    calories_kcal: number;
    protein_g: number;
    fat_g: number;
    saturated_fat_g: number;
    carbohydrates_g: number;
    sugars_g: number;
    dietary_fiber_g: number;
    salt_sodium_g: number;
  } | null;
}>();

const emit = defineEmits<{
  (e: 'imageGenerated', imageDataUrl: string): void;
}>();

const imageDataUrl = ref('');
const loadingShare = ref(false);

const shareFormats = [
  { name: 'storyStarter', id: 'share-story', width: 1080, height: 1920 },
  { name: 'storyMainDish', id: 'share-story', width: 1080, height: 1920 },
  { name: 'storyDessert', id: 'share-story', width: 1080, height: 1920 },
  { name: 'storyFull', id: 'share-story', width: 1080, height: 1920 },
  { name: 'postMainDish', id: 'share-post', width: 1080, height: 1080 },
  { name: 'postFull', id: 'share-post', width: 1080, height: 1080 },
  { name: 'postDessert', id: 'share-post', width: 1080, height: 1080 },
  { name: 'postStarter', id: 'share-post', width: 1080, height: 1080 },
];

async function generateImage() {
  loadingShare.value = true;
  try {
    await nextTick();

    if (props.shareData.imageSrc) {
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => reject(new Error('Image failed to load'));
        img.src = props.shareData.imageSrc;
      });
    }

    // Wait for the logo to finish loading asynchronously
    if (isLogoLoading.value) {
      await new Promise<void>((resolve) => {
        const unwatch = watch(isLogoLoading, (loading) => {
          if (!loading) {
            unwatch();
            resolve();
          }
        });
      });
    }

    // Preload the logo image so html2canvas can render it
    if (logoImageSrc.value) {
      await new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve(); // don't block on logo error
        img.src = logoImageSrc.value;
      });
    }

    const selectedFormat = shareFormats.find((format) => format.name === props.format);
    if (!selectedFormat) return;

    const element = document.getElementById(selectedFormat.id);
    if (!element) return;

    element.style.display = 'block';
    const canvas = await html2canvas(element, {
      scale: 1,
      useCORS: true,
      width: selectedFormat.width,
      height: selectedFormat.height,
    });
    element.style.display = 'none';
    imageDataUrl.value = canvas.toDataURL('image/png');
    emit('imageGenerated', imageDataUrl.value);
  } catch (error) {
    console.error('Error generating image:', error);
  } finally {
    loadingShare.value = false;
  }
}

watch(
  () => props.format,
  () => {
    if (props.format) generateImage();
  },
  { immediate: true },
);

const formattedUrl = (url: string) => url.replace(/^https?:\/\//, '');

const getDishNameSizeClass = (text: string) => {
  const length = text.length;
  console.log('Length: ', length);
  if (length > 61) return 'text-5xl';
  return 'text-6xl';
};
</script>

<template>
  <div>
    <div id="share-story" class="menu-card border-slay-200 absolute relative -left-[5000px] top-0 hidden h-[1920px] w-[1080px] border-2">
      <div v-if="shareData.title" class="mt-[135px] flex h-[150px] w-[1080px] flex-col justify-center px-24">
        <h1 class="line-clamp-1 text-7xl leading-tight text-primary-900">{{ shareData.title }}</h1>
        <h2 v-if="shareData.subtitle" class="text-4xl text-primary-900">
          {{ shareData.subtitle }}
        </h2>
      </div>
      <div class="mx-auto w-[900px]" :class="{ 'mt-[135px]': !shareData.title }">
        <div class="relative mx-auto mb-8 w-full rounded-3xl">
          <div class="absolute -right-[4.5rem] -top-16 flex aspect-square w-[180px] justify-center rounded-full bg-primary-100 align-middle"></div>
          <div class="absolute -right-16 -top-16 flex aspect-square w-44 justify-center rounded-full bg-white align-middle">
            <img v-if="logoImageSrc" :src="logoImageSrc" class="m-auto h-32 w-32 object-contain p-2" />
          </div>
          <img v-if="shareData.imageSrc" :src="shareData.imageSrc" class="h-[780px] w-full rounded-3xl object-cover" />
        </div>
        <div
          v-if="shareData.items.length === 1"
          class="mx-auto mb-20 flex h-[430px] max-w-[900px] flex-col items-center justify-center gap-11 self-stretch"
        >
          <p class="mx-auto line-clamp-3 w-11/12 text-center font-bold font-garamond text-6xl italic leading-normal">
            {{ shareData.items[0].name }}
          </p>
          <!-- <div v-if="shareData.price" class="h-0.5 w-14 bg-gray-300 my-2 mx-auto"></div> -->
          <p v-if="shareData.price" class="w-full text-center font-bold font-garamond text-6xl italic">
            {{ shareData.price }}
          </p>
        </div>
        <div v-else class="mx-auto mb-10 flex h-[600px] max-w-[900px] flex-col items-center justify-center gap-5 self-stretch">
          <div class="flex w-full flex-col justify-between gap-4">
            <p class="mx-auto line-clamp-2 w-11/12 text-center font-bold font-garamond text-5xl italic leading-tight">
              {{ shareData.items.find((item) => item.order === 1)?.name || '***' }}
            </p>
            <div class="mx-auto my-2 h-0.5 w-10 bg-gray-300"></div>
            <p
              class="mx-auto line-clamp-3 w-11/12 text-center font-bold font-garamond italic leading-tight"
              :class="getDishNameSizeClass(shareData.items.find((item) => item.order === 2)?.name || '***')"
            >
              {{ shareData.items.find((item) => item.order === 2)?.name || '***' }}
            </p>
            <div class="mx-auto my-2 h-0.5 w-10 bg-gray-300"></div>
            <p class="mx-auto line-clamp-2 w-11/12 text-center font-bold font-garamond text-5xl italic leading-tight">
              {{ shareData.items.find((item) => item.order === 3)?.name || '***' }}
            </p>
          </div>
          <div v-if="shareData.price" class="mt-2">
            <!-- <div class="h-0.5 w-14 bg-gray-300 mx-auto my-1"></div> -->
            <p class="w-full text-center font-bold font-garamond text-6xl italic">
              {{ shareData.price }}
            </p>
          </div>
        </div>
        <!-- <div v-if="nutritionalData">
          <div class="h-0.5 w-14 bg-gray-300 mx-auto my-1"></div>
          <div class="flex flex-col items-center gap-2">
            <p class="text-4xl font-bold text-primary-900">Nutritional Information</p>
            <div class="flex flex-col items-center gap-1">
              <p class="text-3xl">
                <span class="font-bold">Calories:</span> {{ nutritionalData.calories_kcal }} kcal
              </p>
              <p class="text-3xl">
                <span class="font-bold">Protein:</span> {{ nutritionalData.protein_g }} g
              </p>
              <p class="text-3xl">
                <span class="font-bold">Fat:</span> {{ nutritionalData.fat_g }} g
              </p>
              <p class="text-3xl">
                <span class="font-bold">Saturated Fat:</span> {{ nutritionalData.saturated_fat_g }} g
              </p>
              <p class="text-3xl">
                <span class="font-bold">Carbohydrates:</span> {{ nutritionalData.carbohydrates_g }} g
              </p>
              <p class="text-3xl">
                <span class="font-bold">Sugars:</span> {{ nutritionalData.sugars_g }} g
              </p>
              <p class="text-3xl">
                <span class="font-bold">Dietary Fiber:</span> {{ nutritionalData.dietary_fiber_g }} g
              </p>
              <p class="text-3xl">
                <span class="font-bold">Salt/Sodium:</span> {{ nutritionalData.salt_sodium_g }} g
              </p>
             </div> 
          </div>
        </div> -->

        <footer class="absolute bottom-16 left-0 mx-auto flex w-full flex-row items-end justify-end gap-7">
          <div class="absolute bottom-0 left-12 flex h-28 flex-col items-center">
            <img src="@/assets/images/avatar-head-transparent.svg" alt="Logo restaurant" class="h-20 w-20 object-contain p-1" />
            <p class="w-full text-center text-2xl leading-tight text-primary-700">Powered by</p>
            <p class="w-full text-center font-bold text-2xl text-primary-700">GastronomIA</p>
          </div>
          <div class="flex shrink-0 flex-col items-end text-right text-2xl">
            <p class="w-full font-garamond text-4xl italic">{{ restaurant?.name }}</p>
            <p v-if="restaurant?.phone_number">{{ restaurant.phone_number }}</p>
            <p v-if="restaurant?.website_url">{{ formattedUrl(restaurant.website_url) }}</p>
          </div>
          <div v-if="logoImageSrc" class="mr-12 h-28 w-28">
            <img :src="logoImageSrc" alt="Logo restaurant" class="h-full w-full object-contain p-1" />
          </div>
        </footer>
      </div>
    </div>

    <div id="share-post" class="menu-card border-slay-200 absolute relative -left-[5000px] top-0 hidden h-[1080px] w-[1080px] border-2">
      <div v-if="shareData.title" class="mt-[50px] flex h-[150px] w-[1080px] flex-col justify-center px-24">
        <h1 class="line-clamp-1 text-7xl leading-tight text-primary-900">{{ shareData.title }}</h1>
        <h2 v-if="shareData.subtitle" class="text-4xl text-primary-900">
          {{ shareData.subtitle }}
        </h2>
      </div>
      <div class="mx-auto w-[900px]" :class="{ 'mt-[50px]': !shareData.title }">
        <div
          :class="{ 'h-[830px]': !shareData.title, 'h-[700px]': shareData.title }"
          class="mx-auto mb-20 flex max-w-[900px] flex-col items-center justify-center gap-11 self-stretch"
        >
          <p
            v-if="shareData.items.length === 1"
            class="mx-auto line-clamp-3 w-11/12 text-center font-bold font-garamond text-6xl italic leading-normal"
          >
            {{ shareData.items[0].name }}
          </p>
          <div v-else class="flex w-full flex-col justify-between gap-4">
            <p class="mx-auto line-clamp-2 w-11/12 text-center font-bold font-garamond text-5xl italic leading-tight">
              {{ shareData.items.find((item) => item.order === 1)?.name || '***' }}
            </p>
            <div class="mx-auto my-2 h-0.5 w-10 bg-gray-300"></div>
            <p
              class="mx-auto line-clamp-3 w-11/12 text-center font-bold font-garamond italic leading-tight"
              :class="getDishNameSizeClass(shareData.items.find((item) => item.order === 2)?.name || '***')"
            >
              {{ shareData.items.find((item) => item.order === 2)?.name || '***' }}
            </p>
            <div class="mx-auto my-2 h-0.5 w-10 bg-gray-300"></div>
            <p class="mx-auto line-clamp-2 w-11/12 text-center font-bold font-garamond text-5xl italic leading-tight">
              {{ shareData.items.find((item) => item.order === 3)?.name || '***' }}
            </p>
          </div>
          <div v-if="shareData.price !== ''">
            <!-- <div class="h-0.5 w-14 bg-gray-300 my-2 mx-auto"></div> -->
            <p class="w-full text-center font-bold font-garamond text-6xl italic">
              {{ shareData.price }}
            </p>
          </div>
        </div>
        <footer class="absolute bottom-16 left-0 mx-auto flex w-full flex-row items-end justify-end gap-7">
          <div class="absolute bottom-0 left-12 flex h-28 flex-col items-center">
            <img src="@/assets/images/avatar-head-transparent.svg" alt="Logo restaurant" class="h-20 w-20 object-contain p-1" />
            <p class="w-full text-center text-2xl leading-tight text-primary-700">Powered by</p>
            <p class="w-full text-center font-bold text-2xl text-primary-700">GastronomIA</p>
          </div>
          <div class="flex shrink-0 flex-col items-end text-right text-2xl">
            <p class="w-full font-garamond text-4xl italic">{{ restaurant?.name }}</p>
            <p v-if="restaurant?.phone_number">{{ restaurant.phone_number }}</p>
            <p v-if="restaurant?.website_url">{{ formattedUrl(restaurant.website_url) }}</p>
          </div>
          <div v-if="logoImageSrc" class="mr-12 h-28 w-28">
            <img :src="logoImageSrc" alt="Logo restaurant" class="h-full w-full object-contain p-1" />
          </div>
        </footer>
      </div>
    </div>
  </div>
</template>
